import {Injectable} from '@angular/core';
import {catchError, from, map, mergeMap, Observable, of, switchMap, toArray} from 'rxjs';
import {ApiwrapperService} from '../../services/apiwrapper.service';
import {DataManipulationService} from '../../services/data-manipulation.service';
import {environment} from '../../../environments/environment';
import {
  LoadedTeam,
  MembersPage,
  OrganizeMember,
  SaveChange,
  SaveResult,
  SourceTeam,
  TeamAssignment,
  TeamLoadOutcome,
  TeamRoleKind,
  TeamRoles,
  TeamType
} from './organize-teams.models';

export const TEAM_LOAD_CONCURRENCY = 3;
export const SAVE_CONCURRENCY = 4;

const LEADER_PRIORITY = 20;
const MEMBER_PRIORITY = 10;

interface ApiUser {
  uuid: string;
  email: string;
  avatar: { name?: string } | null;
  detail: {
    firstname: string | null;
    lastname: string | null;
    displayName: string | null;
  };
  startGuideTeamMember?: number | null;
  startGuidTeamLeader?: number | null;
  teamRole?: { uuid: string; teamPriority: number | null } | null;
}

interface ApiWorkspaceUser {
  uuid: string;
  user: ApiUser;
}

interface ApiWorkspaceUsersPage {
  _page?: number;
  _page_count: number;
  _embedded: { users: ApiWorkspaceUser[] };
}

interface ApiQuestionCollection {
  slug: string;
  title: string;
}

interface ApiTeamMember {
  uuid: string;
  status: string;
  user: ApiUser;
  team?: { questionCollections?: ApiQuestionCollection[] | null };
}

interface ApiRole {
  uuid: string;
  name: string;
}

interface ApiErrorResponse {
  error?: { error?: { messages?: string[] } };
}

export function extractApiErrorMessage(err: unknown): string | null {
  const messages = (err as ApiErrorResponse | null)?.error?.error?.messages;
  return messages && messages.length > 0 ? messages[0] : null;
}

@Injectable()
export class OrganizeTeamsDataService {

  constructor(private api: ApiwrapperService, private data: DataManipulationService) {
  }

  loadRoles(): Observable<TeamRoles> {
    return this.api.getAllRoles('team').pipe(
      map((roles: ApiRole[]) => {
        const leader = roles.find(role => role.name === 'team-admin');
        const member = roles.find(role => role.name === 'team-member');
        if (!leader || !member) {
          throw new Error('Team roles are missing');
        }
        return {
          leader: {uuid: leader.uuid, name: leader.name},
          member: {uuid: member.uuid, name: member.name}
        };
      })
    );
  }

  loadMembersPage(page: number): Observable<MembersPage> {
    return this.api.getWorkspacesUserWithPagination(page).pipe(
      map((res: ApiWorkspaceUsersPage) => ({
        members: res._embedded.users.map(entry => this.toWorkspaceMember(entry)),
        page: res._page ?? page,
        pageCount: res._page_count
      }))
    );
  }

  loadTeam(team: SourceTeam): Observable<LoadedTeam> {
    const rows$: Observable<ApiTeamMember[]> = team.totalMembers === 0 ? of([]) : this.api.getTeamMembers(team.uuid);
    return rows$.pipe(
      switchMap(rows => {
        const teamType$ = rows.length > 0
          ? of(this.toTeamType(rows[0].team?.questionCollections))
          : this.api.getTeamQuestionCollections(team.uuid).pipe(
            map((collections: ApiQuestionCollection[]) => this.toTeamType(collections)),
            catchError(() => of(null))
          );
        return teamType$.pipe(map(teamType => this.toLoadedTeam(team.uuid, teamType, rows)));
      })
    );
  }

  loadTeams(teams: SourceTeam[]): Observable<TeamLoadOutcome> {
    return from(teams).pipe(
      mergeMap(team => this.loadTeam(team).pipe(
        map((loaded): TeamLoadOutcome => ({teamUuid: team.uuid, team: loaded})),
        catchError(() => of<TeamLoadOutcome>({teamUuid: team.uuid, team: null}))
      ), TEAM_LOAD_CONCURRENCY)
    );
  }

  searchMembers(term: string): Observable<OrganizeMember[]> {
    return this.api.getSearchWorkspaceMember(term).pipe(
      map((users: ApiWorkspaceUser[]) => users.map(entry => this.toWorkspaceMember(entry)))
    );
  }

  getInviteLink(teamUuid: string): Observable<string> {
    return this.api.getTeamInvitation(teamUuid).pipe(
      map((res: { hash: string }) => environment.frontendUrl + '/add-to-workspace/' + res.hash)
    );
  }

  inviteToWorkspace(email: string, workspaceRoleUuid: string): Observable<OrganizeMember | null> {
    return this.api.inviteUserToWorkspace({emails: [email], roleUuid: workspaceRoleUuid}).pipe(
      switchMap(() => this.searchMembers(email).pipe(catchError(() => of<OrganizeMember[]>([])))),
      map(members => members.find(member => member.email === email) ?? members[0] ?? null)
    );
  }

  save(changes: SaveChange[], roles: TeamRoles): Observable<SaveResult[]> {
    if (changes.length === 0) {
      return of([]);
    }
    return from(changes).pipe(
      mergeMap(change => this.saveChange(change, roles), SAVE_CONCURRENCY),
      toArray()
    );
  }

  removeFromTeam(membershipUuid: string, teamUuid: string): Observable<void> {
    return this.api.deleteTeamMember(membershipUuid, teamUuid).pipe(map(() => undefined));
  }

  removeFromWorkspace(userUuid: string): Observable<void> {
    return this.api.deleteWorkspaceUser(userUuid).pipe(map(() => undefined));
  }

  private saveChange(change: SaveChange, roles: TeamRoles): Observable<SaveResult> {
    const body = {userUuid: change.userUuid, roleUuid: roles[change.role].uuid};
    const request$: Observable<SaveResult> = change.kind === 'add'
      ? this.api.postAddMemberToTeam(body, change.teamUuid).pipe(
        map((res: ApiTeamMember | null) => this.successResult(change, res?.uuid ?? null, res?.status === 'active'))
      )
      : this.api.postMemberRole(body.userUuid, body.roleUuid, change.teamUuid).pipe(
        map(() => this.successResult(change, null, true))
      );
    return request$.pipe(
      catchError(err => of<SaveResult>({
        change,
        ok: false,
        membershipUuid: null,
        active: false,
        errorMessage: extractApiErrorMessage(err)
      }))
    );
  }

  private successResult(change: SaveChange, membershipUuid: string | null, active: boolean): SaveResult {
    return {change, ok: true, membershipUuid, active, errorMessage: null};
  }

  private toLoadedTeam(teamUuid: string, teamType: TeamType | null, rows: ApiTeamMember[]): LoadedTeam {
    const members: OrganizeMember[] = [];
    const assignments: TeamAssignment[] = [];
    rows.forEach(row => {
      const role = this.toRoleKind(row.user.teamRole?.teamPriority);
      if (!role) {
        return;
      }
      members.push(this.toMember(row.user, false));
      assignments.push({userUuid: row.user.uuid, role, originalRole: role, membershipUuid: row.uuid});
    });
    return {teamUuid, teamType, members, assignments};
  }

  private toRoleKind(teamPriority: number | null | undefined): TeamRoleKind | null {
    if (teamPriority === LEADER_PRIORITY) {
      return 'leader';
    }
    if (teamPriority === MEMBER_PRIORITY) {
      return 'member';
    }
    return null;
  }

  private toTeamType(collections: ApiQuestionCollection[] | null | undefined): TeamType | null {
    if (!collections || collections.length === 0) {
      return null;
    }
    const last = collections[collections.length - 1];
    const slug = last.slug.toLowerCase();
    return {slug: slug === 'physical-workplace' ? 'office' : slug, title: last.title};
  }

  private toWorkspaceMember(entry: ApiWorkspaceUser): OrganizeMember {
    const assignedCount = (entry.user.startGuideTeamMember ?? 0) + (entry.user.startGuidTeamLeader ?? 0);
    return this.toMember(entry.user, assignedCount > 0);
  }

  private toMember(user: ApiUser, hasServerAssignment: boolean): OrganizeMember {
    const displayName = String(this.data.getUserDisplayName(user));
    return {
      userUuid: user.uuid,
      email: user.email,
      displayName,
      label: displayName === user.email ? user.email : displayName + ' (' + user.email + ')',
      avatarUrl: user.avatar?.name ? this.data.baseUrl + '/uploads/user/' + user.uuid + '/' + user.avatar.name : null,
      hasServerAssignment
    };
  }
}
